import { createContext, FC, useCallback, useMemo, useState } from 'react';

export type PiPContextType = {
  isSupported: boolean;
  pipWindow: Window | null;
  requestPipWindow: (width: number, height: number) => Promise<void>;
  closePipWindow: () => void;
};

export const PiPContext = createContext<PiPContextType | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};
/**
 * Zoom公式の例
 * https://developers.zoom.us/docs/video-sdk/web/video/#start-video
 *
 * MDNのPiPサンプル
 * https://github.com/mdn/dom-examples/blob/main/document-picture-in-picture/main.js
 *
 * Reactの例
 * https://hackernoon.com/how-to-document-picture-in-picture-in-react-with-typescript
 */
const PiPProvider: FC<Props> = ({ children }) => {
  const isSupported = 'documentPictureInPicture' in window;
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const closePipWindow = useCallback(() => {
    if (pipWindow != null) {
      pipWindow.close();
      setPipWindow(null);
    }
  }, [pipWindow]);
  const requestPipWindow = useCallback(
    async (width: number, height: number) => {
      // We don't want to allow multiple requests.
      if (pipWindow != null) {
        return;
      }

      const pip = await window.documentPictureInPicture.requestWindow({
        width,
        height
      });

      // Detect when window is closed by user
      pip.addEventListener('pagehide', () => {
        setPipWindow(null);
      });

      // It is important to copy all parent widnow styles. Otherwise, there would be no CSS available at all
      // https://developer.chrome.com/docs/web-platform/document-picture-in-picture/#copy-style-sheets-to-the-picture-in-picture-window
      //   [...document.styleSheets].forEach((styleSheet) => {
      //     try {
      //       const cssRules = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
      //       const style = document.createElement('style');

      //       style.textContent = cssRules;
      //       pip.document.head.appendChild(style);
      //     } catch (e) {
      //       const link = document.createElement('link');
      //       if (styleSheet.href == null) {
      //         return;
      //       }

      //       link.rel = 'stylesheet';
      //       link.type = styleSheet.type;
      //       link.media = styleSheet.media.toString();
      //       link.href = styleSheet.href;
      //       pip.document.head.appendChild(link);
      //     }
      //   });

      setPipWindow(pip);
    },
    [pipWindow]
  );

  const value = useMemo(() => {
    {
      return {
        isSupported,
        pipWindow,
        requestPipWindow,
        closePipWindow
      };
    }
  }, [closePipWindow, isSupported, pipWindow, requestPipWindow]);
  return <PiPContext.Provider value={value}>{children}</PiPContext.Provider>;
};
export default PiPProvider;
